import KeenSlider, { KeenSliderInstance } from "keen-slider";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { watch } from "vue";

interface SliderInstance {
    instance: KeenSliderInstance | null;
    dotsContainer: HTMLElement | null;
}

const isTabletOrBigger = useBreakpoints("sm");

const sliderInstances: Map<HTMLElement, SliderInstance> = new Map();

function initializeSlider(container: HTMLElement) {
    destroyExistingSlider(container);

    const sliderInstance = new KeenSlider(container, {
        slides: { perView: 1 },
        loop: true,
        mode: "snap",
        slideChanged() {
            updateDots(container, sliderInstance.track.details.rel);
        },
    });

    const dotsContainer = createDots(container, sliderInstance);
    sliderInstances.set(container, { instance: sliderInstance, dotsContainer });
    updateDots(container, 0);
}

function destroyExistingSlider(container: HTMLElement) {
    const existingSlider = sliderInstances.get(container);
    if (existingSlider?.instance) {
        existingSlider.instance.destroy();
    }
}

function createDots(container: HTMLElement, sliderInstance: KeenSliderInstance) {
    const dotContainer = document.createElement("ol");
    dotContainer.classList.add("mobile-carousel__dots");

    const slides = container.querySelectorAll<HTMLElement>(".c-12");
    slides.forEach((_, idx) => {
        const dot = document.createElement("li");
        dot.classList.add("mobile-carousel__dot");
        dot.dataset.index = idx.toString();
        dot.ariaLabel = (idx + 1).toString();
        dot.addEventListener("click", () => sliderInstance.moveToIdx(idx));
        dotContainer.appendChild(dot);
    });

    container.parentNode?.appendChild(dotContainer);
    return dotContainer;
}

function updateDots(container: HTMLElement, activeIndex: number) {
    const sliderData = sliderInstances.get(container);
    if (sliderData?.dotsContainer) {
        const dots = sliderData.dotsContainer.querySelectorAll<HTMLElement>(".mobile-carousel__dot");
        dots.forEach((dot, idx) => {
            dot.classList.toggle("mobile-carousel__dot--active", idx === activeIndex);
        });
    }
}

function handleResize() {
    const mobileCarouselElements = document.querySelectorAll<HTMLElement>(".mobile-carousel");

    mobileCarouselElements.forEach((mobileCarouselElement) => {
        if (isTabletOrBigger.value) {
            destroyMobileSlider(mobileCarouselElement);
        } else {
            initializeMobileSlider(mobileCarouselElement);
        }
    });
}

function initializeMobileSlider(container: HTMLElement) {
    if (!sliderInstances.has(container)) {
        container.classList.add("keen-slider");
        container.querySelectorAll<HTMLElement>(".c-12").forEach((slide) => slide.classList.add("keen-slider__slide"));
        initializeSlider(container);
    }
}

function destroyMobileSlider(container: HTMLElement) {
    if (sliderInstances.has(container)) {
        container.classList.remove("keen-slider");
        container
            .querySelectorAll<HTMLElement>(".c-12")
            .forEach((slide) => slide.classList.remove("keen-slider__slide"));
        const sliderData = sliderInstances.get(container);
        sliderData?.dotsContainer?.remove();
        sliderData?.instance?.destroy();
        sliderInstances.delete(container);
    }
}

watch(isTabletOrBigger, () => {
    handleResize();
});

document.ready(() => {
    handleResize();
});
